import { useState, useEffect, useCallback, useRef, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  X,
  FileUp,
  Package,
  SlidersHorizontal,
  ShoppingCart,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../hooks/useApp';
import { supabase } from '../lib/supabase';
import ArbiToggle from '../components/ui/ArbiToggle';
import { triggerTicker } from '../lib/activityTicker';
import AlertsStrip, { type DashboardAlert } from '../components/dashboard/AlertsStrip';
import MetricCards from '../components/dashboard/MetricCards';
import PipelineStrips from '../components/dashboard/PipelineStrips';
import ChartsRow, { type RevenueDatum, type RoiDatum } from '../components/dashboard/ChartsRow';
import ActivityFeed, { type ActivityItem } from '../components/dashboard/ActivityFeed';

// ── Getting Started Checklist ─────────────────────────────────────────────

type ChecklistStepKey = 'imports' | 'products' | 'params' | 'sessions' | 'pos';

interface ChecklistStep {
  key: ChecklistStepKey;
  title: string;
  desc: string;
  cta: string;
  link: string;
  icon: LucideIcon;
}

const CHECKLIST_STEPS: ChecklistStep[] = [
  {
    key: 'imports',
    title: 'Import your first supplier price sheet',
    desc: 'Upload an Algopix-enriched file to populate your product catalog',
    cta: 'Go to Files',
    link: '/files',
    icon: FileUp,
  },
  {
    key: 'products',
    title: 'Review products in the catalog',
    desc: 'Check your imported products and resolve any flagged items',
    cta: 'Open Catalog',
    link: '/catalog/products',
    icon: Package,
  },
  {
    key: 'params',
    title: 'Configure your buying parameters',
    desc: 'Set your margins, fees, and buying criteria in Settings',
    cta: 'Open Settings',
    link: '/settings',
    icon: SlidersHorizontal,
  },
  {
    key: 'sessions',
    title: 'Run a buying session',
    desc: 'Analyze profitability and stage products for a purchase order',
    cta: 'Start Buying',
    link: '/purchasing/buying-session',
    icon: ShoppingCart,
  },
  {
    key: 'pos',
    title: 'Submit your first PO',
    desc: 'Build and submit a purchase order to your distributor',
    cta: 'Build a PO',
    link: '/purchasing/po-builder',
    icon: FileText,
  },
];

// Counts returned by get_dashboard_checklist_counts RPC — shape mirrored from
// the function the Dashboard queries; documented here until the RPC is typed.
interface ChecklistCounts {
  ingestion_batches?: number;
  products?: number;
  params?: number;
  buying_sessions?: number;
  purchase_orders?: number;
  files?: number;
}

interface GettingStartedChecklistProps {
  counts: ChecklistCounts | null;
  onDismiss: () => void;
}

function GettingStartedChecklist({ counts, onDismiss }: GettingStartedChecklistProps): JSX.Element {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const completions: Record<ChecklistStepKey, boolean> = {
    imports: (counts?.ingestion_batches || 0) > 0,
    products: (counts?.products || 0) > 0,
    params: (counts?.params || 0) > 0,
    sessions: (counts?.buying_sessions || 0) > 0,
    pos: (counts?.purchase_orders || 0) > 0,
  };
  const completedCount = Object.values(completions).filter(Boolean).length;
  const allDone = completedCount === CHECKLIST_STEPS.length;

  // Auto-collapse (not dismiss) when all complete
  useEffect(() => {
    if (allDone && !collapsed) {
      const t = setTimeout(() => setCollapsed(true), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [allDone, collapsed]);

  // Collapsed state — show summary line
  if (collapsed) {
    return (
      <div className="bg-surface-1 border border-border rounded-lg px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Check size={16} className="text-success" />
          <span className="text-xs text-success font-medium">All set up</span>
        </div>
        <div className="flex items-center gap-2">
          {!allDone && (
            <button
              onClick={() => setCollapsed(false)}
              className="text-2xs text-accent hover:text-accent-hover transition-colors cursor-pointer"
            >
              Show steps
            </button>
          )}
          <button
            onClick={onDismiss}
            className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
            aria-label="Dismiss checklist"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-ink-bright">Get started with Arbi</h2>
          <p className="text-xs text-ink-muted mt-0.5">
            Complete these steps to set up your account
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-muted font-mono">
            {completedCount} of {CHECKLIST_STEPS.length} steps
          </span>
          <button
            onClick={onDismiss}
            className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
            aria-label="Dismiss checklist"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-surface-3 rounded-full mb-4">
        <div
          className="h-full bg-accent rounded-full transition-all duration-300"
          style={{ width: `${(completedCount / CHECKLIST_STEPS.length) * 100}%` }}
        />
      </div>

      {allDone ? (
        <div className="text-center py-4">
          <Check size={24} className="text-success mx-auto mb-2" />
          <p className="text-sm text-success font-medium">You&apos;re all set!</p>
        </div>
      ) : (
        <div className="space-y-1">
          {CHECKLIST_STEPS.map((step, idx) => {
            const done = completions[step.key];
            return (
              <div key={step.key} className="flex items-center gap-3 py-2 px-1 rounded-md">
                {/* Circle indicator */}
                <div
                  className={clsx(
                    'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-2xs font-semibold',
                    done ? 'bg-accent text-white' : 'border border-border text-ink-muted'
                  )}
                >
                  {done ? <Check size={12} /> : idx + 1}
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p
                    className={clsx(
                      'text-xs font-medium',
                      done ? 'line-through text-ink-muted' : 'text-ink-primary'
                    )}
                  >
                    {step.title}
                  </p>
                  {!done && <p className="text-xs text-ink-muted mt-0.5">{step.desc}</p>}
                </div>
                {/* CTA */}
                {!done && (
                  <button
                    onClick={() => navigate(step.link)}
                    className="arbi-btn-ghost text-2xs px-2 py-1 flex-shrink-0"
                  >
                    {step.cta}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Dashboard ───────────────────────────────────────────────────────────────

interface DashboardMetrics {
  active_pos: number;
  open_orders: number;
  open_returns: number;
}

type PipelineCounts = Record<string, Record<string, number>>;

// Shape of notification rows read by the activity feed query.
interface NotificationActivityRow {
  id: string;
  event: string | null;
  title: string | null;
  message: string | null;
  link: string | null;
  created_at: string;
}

interface CreditLedgerActivityRow {
  id: string;
  event_type: string | null;
  amount: number;
  notes: string | null;
  created_at: string;
}

export default function Dashboard(): JSX.Element {
  const { user, isAdmin, fullName } = useApp();

  // ── Data state ──
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    active_pos: 0,
    open_orders: 0,
    open_returns: 0,
  });
  const [pipelineCounts, setPipelineCounts] = useState<PipelineCounts>({});
  const [revenueData] = useState<RevenueDatum[]>([]);
  const [roiData] = useState<RoiDatum[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [showAllActivity, setShowAllActivity] = useState<boolean>(false);

  // ── Checklist state ──
  const [checklistCounts, setChecklistCounts] = useState<ChecklistCounts | null>(null);
  const [checklistDismissed, setChecklistDismissed] = useState<boolean>(
    () => localStorage.getItem('arbi_checklist_dismissed') === 'true'
  );

  const dismissChecklist = useCallback((): void => {
    setChecklistDismissed(true);
    localStorage.setItem('arbi_checklist_dismissed', 'true');
  }, []);

  // Show checklist if not dismissed and counts are zero
  const showChecklist =
    !checklistDismissed &&
    checklistCounts &&
    (checklistCounts.products || 0) === 0 &&
    (checklistCounts.purchase_orders || 0) === 0 &&
    (checklistCounts.files || 0) === 0;

  // ── Fetch checklist counts ──
  useEffect(() => {
    if (!user || checklistDismissed) return;
    supabase.rpc('get_dashboard_checklist_counts').then(
      ({ data }) => {
        if (data) setChecklistCounts(data as unknown as ChecklistCounts);
      },
      (e: Error) => console.error('[Dashboard] checklist counts error:', e)
    );
  }, [user, checklistDismissed]);

  // ── Fetch metrics ──
  const fetchMetrics = useCallback(async (): Promise<void> => {
    try {
      const { data } = await supabase.rpc('get_dashboard_metrics');
      if (data) setMetrics(data as unknown as DashboardMetrics);
    } catch (e) {
      console.error('[Dashboard] metrics error:', e);
    }
  }, []);

  const fetchPipelineCounts = useCallback((): void => {
    supabase.rpc('get_inventory_counts').then(
      ({ data }) => {
        if (data)
          setPipelineCounts((prev) => ({
            ...prev,
            inventory: data as unknown as Record<string, number>,
          }));
      },
      (e: Error) => console.error('[Dashboard] inventory counts error:', e)
    );
    supabase.rpc('get_fulfillment_counts').then(
      ({ data }) => {
        if (data)
          setPipelineCounts((prev) => ({
            ...prev,
            fulfillment: data as unknown as Record<string, number>,
          }));
      },
      (e: Error) => console.error('[Dashboard] fulfillment counts error:', e)
    );
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchMetrics();
    fetchPipelineCounts();
  }, [user, fetchMetrics, fetchPipelineCounts]);

  // ── Real-time metric + pipeline revalidation via Supabase Realtime ──
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!user) return undefined;

    const debouncedRefresh = (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchMetrics();
        fetchPipelineCounts();
      }, 500);
    };

    const channel = supabase
      .channel('dashboard-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'purchase_orders' },
        debouncedRefresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'fulfillment_orders' },
        debouncedRefresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'inventory_items' },
        debouncedRefresh
      )
      .on('postgres_changes', { event: '*', schema: 'public', table: 'returns' }, debouncedRefresh)
      .subscribe();

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [user, fetchMetrics, fetchPipelineCounts]);

  // ── Activity feed ──
  const fetchActivities = useCallback(async (): Promise<void> => {
    if (!user) return;
    try {
      const items: ActivityItem[] = [];

      // 1. Notifications (user-scoped or all for admin)
      let nQuery = supabase
        .from('notifications')
        .select('id, event, title, message, link, created_at')
        .order('created_at', { ascending: false })
        .limit(30);
      if (!showAllActivity) {
        nQuery = nQuery.eq('user_id', user.id);
      }
      const { data: notifs } = await nQuery;
      for (const n of (notifs ?? []) as NotificationActivityRow[]) {
        const typeMap: Record<string, string> = {
          po_submitted: 'po_created',
          po_approved: 'po_approved',
          po_rejected: 'po_created',
          po_cancelled: 'po_created',
          po_edit_required: 'po_created',
          po_chain_cancelled: 'po_created',
          po_received: 'po_received',
          receiving_discrepancy: 'alert_triggered',
          file_uploaded: 'import_complete',
          task_assigned: 'user_action',
        };
        items.push({
          id: n.id,
          type: (n.event && typeMap[n.event]) || 'user_action',
          description: n.message || n.title || '',
          created_at: n.created_at,
          action_url: n.link,
        });
      }

      // 2. Credit ledger events
      const { data: ledgerRows } = await supabase
        .from('credit_ledger')
        .select('id, event_type, amount, notes, created_at')
        .order('created_at', { ascending: false })
        .limit(20);
      for (const row of (ledgerRows ?? []) as CreditLedgerActivityRow[]) {
        const typeMap: Record<string, string> = {
          soft_reserve: 'po_approved',
          hard_draw: 'po_received',
          reserve_release: 'po_approved',
          adjustment: 'alert_triggered',
        };
        items.push({
          id: `cl-${row.id}`,
          type: (row.event_type && typeMap[row.event_type]) || 'user_action',
          description:
            row.notes || `Credit ${row.event_type ?? 'event'}: $${Math.abs(row.amount).toFixed(2)}`,
          created_at: row.created_at,
        });
      }

      // Sort combined and take top 50
      items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setActivities(items.slice(0, 50));
    } catch (e) {
      console.error('[Dashboard] activity feed error:', e);
    }
  }, [user, showAllActivity]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // ── Realtime subscription for activity feed (10s throttle) ──
  const activityThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityFetchRef = useRef<number>(0);
  useEffect(() => {
    if (!user) return undefined;

    const throttledRefresh = (): void => {
      const now = Date.now();
      if (now - lastActivityFetchRef.current < 10000) {
        if (!activityThrottleRef.current) {
          activityThrottleRef.current = setTimeout(
            () => {
              activityThrottleRef.current = null;
              lastActivityFetchRef.current = Date.now();
              fetchActivities();
            },
            10000 - (now - lastActivityFetchRef.current)
          );
        }
        return;
      }
      lastActivityFetchRef.current = now;
      fetchActivities();
    };

    const withTicker = (msg: string) => (): void => {
      triggerTicker(msg);
      throttledRefresh();
    };

    const channel = supabase
      .channel('dashboard-activity-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications' },
        throttledRefresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'credit_ledger' },
        throttledRefresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'purchase_orders' },
        withTicker('PO updated')
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'fulfillment_orders' },
        withTicker('Order updated')
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'returns' },
        withTicker('Return updated')
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        withTicker('Task updated')
      )
      .subscribe();

    return () => {
      if (activityThrottleRef.current) clearTimeout(activityThrottleRef.current);
      supabase.removeChannel(channel);
    };
  }, [user, fetchActivities]);

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-5xl mx-auto p-5 space-y-4">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-ink-bright">Dashboard</h1>
          <p className="text-sm text-ink-muted mt-0.5">
            {fullName
              ? `Welcome back, ${fullName.split(' ')[0]}.`
              : user?.email
                ? `Welcome back, ${user.email.split('@')[0]}.`
                : 'Welcome back.'}
          </p>
        </div>

        {/* 0. Getting Started Checklist (first-run only) */}
        {showChecklist && (
          <GettingStartedChecklist counts={checklistCounts} onDismiss={dismissChecklist} />
        )}

        {/* 1. Alerts strip — hidden if no alerts */}
        <AlertsStrip
          alerts={alerts}
          onResolve={(alert) => setAlerts((prev) => prev.filter((a) => a.id !== alert.id))}
          onReview={() => {}}
        />

        {/* 2. Metric cards */}
        <MetricCards
          activePOs={{ count: metrics.active_pos, value: 0 }}
          openOrders={{ count: metrics.open_orders, value: 0 }}
          openReturns={{ count: metrics.open_returns, value: 0 }}
        />

        {/* 3. Pipeline strips */}
        <PipelineStrips counts={pipelineCounts} />

        {/* 4. Charts */}
        <ChartsRow revenueData={revenueData} roiData={roiData} />

        {/* 5. Activity feed */}
        <div>
          {isAdmin && (
            <div className="flex items-center gap-2 mb-2">
              <ArbiToggle
                checked={showAllActivity}
                onChange={setShowAllActivity}
                label="Show all users' activity"
              />
              <span className="text-xs text-ink-secondary">Show all users</span>
            </div>
          )}
          <ActivityFeed activities={activities} />
        </div>
      </div>
    </div>
  );
}
